import React, { useMemo, useState } from 'react';
import {
    Checkbox,
    DefaultButton,
    IconButton,
    Modal,
    PrimaryButton,
    Stack,
    Text,
    useTheme,
} from '@fluentui/react';
import { IStorefrontSection, IStorefrontStyle } from '../../../models/Storefront';
import SectionRealPreview, { MIN_PREVIEW_COLUMN } from './SectionRealPreview';
import { sectionWithStyleRef } from '../Editor/Sections/sectionStyleRef';
import { isGlobalStyle } from '../Editor/stylesConfig';

// Picking one of the organization's own named section styles for a section, from inside the apply-template
// flow.
//
// It is a modal rather than a fourth entry in the dropdown because choosing a style is two questions, not
// one: WHICH style, and whether it should go to this section alone or to every section like it. A dropdown
// can only answer the first, and answering the second by guessing is how a customer ends up restyling a page
// they meant to touch in one place.
//
// The preview is the real renderer, exactly as on the wizard's sections step — see SectionRealPreview. A
// named style is the one case where a merchant most needs to see the result before committing, because the
// style's content lives somewhere else entirely and its name says nothing about what it looks like.

interface ISectionStylePickerModalProps {
    isOpen: boolean;
    /** The section being decided about, for the before/after. */
    section?: IStorefrontSection;
    storefrontId: string;
    /** The organization's section-type styles. The caller only opens this when there is at least one. */
    styles: IStorefrontStyle[];
    /** The style already chosen for this section, if the picker is being reopened to change it. */
    currentStyleId?: string;
    /** How many OTHER sections of the same kind are in play, for the apply-to-all offer. */
    likeCount: number;
    /** What that kind is called, e.g. "product sections". */
    likeLabel: string;
    onApply: (styleId: string, applyToLike: boolean) => void;
    onDismiss: () => void;
}

const SectionStylePickerModal: React.FC<ISectionStylePickerModalProps> = (props) => {
    const theme = useTheme();
    // Seeded from the current choice so reopening the picker lands on what is already applied rather than on
    // the first style in the list.
    const [selectedId, setSelectedId] = useState<string | undefined>(props.currentStyleId);
    const [applyToLike, setApplyToLike] = useState(false);

    // The modal is mounted by the wizard and kept mounted, so its state has to be re-seeded each time it
    // opens — otherwise the second section decided in one sitting inherits the first one's answers.
    const openedFor = `${props.isOpen ? 'open' : 'closed'}:${props.section?.id ?? ''}:${props.currentStyleId ?? ''}`;
    const lastOpened = React.useRef(openedFor);
    if (lastOpened.current !== openedFor) {
        lastOpened.current = openedFor;
        setSelectedId(props.currentStyleId);
        setApplyToLike(false);
    }

    const preview = useMemo(
        () => (props.section && selectedId ? sectionWithStyleRef(props.section, selectedId) : undefined),
        [props.section, selectedId]);

    const selectedName = props.styles.find((s) => s.id === selectedId)?.name;

    return (
        <Modal
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            isBlocking={false}
            styles={{ main: { maxWidth: 1100, width: '90vw' } }}
        >
            <Stack tokens={{ childrenGap: '1em' }} styles={{ root: { padding: '1.5em' } }}>
                <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
                    <Text variant='xLarge'>Use a section style</Text>
                    <IconButton iconProps={{ iconName: 'Cancel' }} ariaLabel='Close' onClick={props.onDismiss} />
                </Stack>

                <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    These are your organization&apos;s own saved section styles. Applying one replaces this
                    section&apos;s own styling with a reference to it, so the section follows the style from
                    then on and changing the style changes the section with it.
                </Text>

                <Stack horizontal wrap tokens={{ childrenGap: '1em' }} verticalAlign='stretch'>
                    <Stack.Item styles={{ root: { minWidth: 200, maxWidth: 260 } }}>
                        <Stack tokens={{ childrenGap: 4 }}>
                            {props.styles.map((style) => {
                                const selected = style.id === selectedId;
                                return (
                                    <div
                                        key={style.id}
                                        role='button'
                                        tabIndex={0}
                                        onClick={() => setSelectedId(style.id)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setSelectedId(style.id);
                                            }
                                        }}
                                        style={{
                                            border: `${selected ? 2 : 1}px solid ${selected
                                                ? theme.palette.themePrimary
                                                : theme.palette.neutralQuaternary}`,
                                            borderRadius: 2,
                                            // Keeps the row from shifting as the border thickens on selection.
                                            padding: selected ? '0.5em 0.75em' : 'calc(0.5em + 1px) calc(0.75em + 1px)',
                                            cursor: 'pointer',
                                            background: theme.palette.white,
                                            textAlign: 'left',
                                        }}
                                    >
                                        <Text block variant='small' styles={{ root: { fontWeight: 600 } }}>
                                            {style.name}
                                        </Text>
                                        {/* A global style is shared with every storefront in the
                                            organization, so editing it later reaches further than this
                                            page — worth knowing before picking it. */}
                                        {isGlobalStyle(style) && (
                                            <Text block variant='xSmall' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                                                Global
                                            </Text>
                                        )}
                                    </div>
                                );
                            })}
                        </Stack>
                    </Stack.Item>

                    <Stack.Item grow styles={{ root: { flexBasis: 0, minWidth: MIN_PREVIEW_COLUMN } }}>
                        {props.section && (
                            <Stack horizontal wrap tokens={{ childrenGap: 8 }} verticalAlign='stretch'>
                                <Stack.Item grow styles={{ root: { flexBasis: 0, minWidth: MIN_PREVIEW_COLUMN } }}>
                                    <SectionRealPreview
                                        label='Now'
                                        section={props.section}
                                        storefrontId={props.storefrontId}
                                        muted={!!preview}
                                    />
                                </Stack.Item>
                                {preview && (
                                    <Stack.Item grow styles={{ root: { flexBasis: 0, minWidth: MIN_PREVIEW_COLUMN } }}>
                                        <SectionRealPreview
                                            label={`With ${selectedName}`}
                                            section={preview}
                                            storefrontId={props.storefrontId}
                                        />
                                    </Stack.Item>
                                )}
                            </Stack>
                        )}
                        {!selectedId && (
                            <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                                Choose a style to see what it does to this section.
                            </Text>
                        )}
                    </Stack.Item>
                </Stack>

                {/* Offered only when there is more than one section of this kind: a checkbox that can only
                    mean "and the zero others" is noise. */}
                {props.likeCount > 0 && (
                    <Checkbox
                        label={`Also apply it to the other ${props.likeCount} ${props.likeLabel}`}
                        checked={applyToLike}
                        onChange={(_e, checked) => setApplyToLike(!!checked)}
                    />
                )}

                <Stack horizontal tokens={{ childrenGap: '0.5em' }} horizontalAlign='end'>
                    <PrimaryButton
                        text='Use this style'
                        disabled={!selectedId}
                        onClick={() => selectedId && props.onApply(selectedId, applyToLike)}
                    />
                    <DefaultButton text='Cancel' iconProps={{ iconName: 'Cancel' }} onClick={props.onDismiss} />
                </Stack>
            </Stack>
        </Modal>
    );
};

export default SectionStylePickerModal;
